import React, { Component, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Button, Card, Col, Container, Image, Row, Alert } from 'react-bootstrap';
import Nouislider from "nouislider-react";
import 'nouislider/distribute/nouislider.css';
import './gaugeBar.scss';
import { Toast } from 'primereact/toast';
import ErrorWithReload from '../charts/ErrorWithReload';
import { useSelector } from 'react-redux';
import NumberFormatter from '../../formatters/NumberFormatter';

const GaugeBar = (props) => {  // props: {title, sliderValue, sliderRange, data, onReload}
    const toast = useRef(null);
    const [isSliderError, setIsSliderError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('Slider verisi yüklenirken bir hata oluştu');
    const selectedSecurity = useSelector((state) => state.security?.selectedSecurity);
    const [symbolCode, setSymbolCode] = useState(selectedSecurity.code);
    const [symbolName, setSymbolName] = useState(selectedSecurity.name);
    const numberFormatter = new NumberFormatter();

    // Combined useEffect for validation and symbol updates
    const prevProps = useRef({
        sliderValue: null,
        sliderRange: null,
        code: null,
        name: null
    });

    useEffect(() => {

        // Only validate if props have actually changed
        const sliderValueChanged = JSON.stringify(prevProps.current.sliderValue) !== JSON.stringify(props.sliderValue);
        const sliderRangeChanged = JSON.stringify(prevProps.current.sliderRange) !== JSON.stringify(props.sliderRange);
        console.log("gaugeLabel: ", props.gaugeLabel);
        if (sliderValueChanged || sliderRangeChanged) {
            validateProps();
            prevProps.current.sliderValue = props.sliderValue;
            prevProps.current.sliderRange = props.sliderRange;
        }

        // Only update symbol data if it has changed
        if (selectedSecurity.code !== prevProps.current.code && selectedSecurity.code) {
            setSymbolCode(selectedSecurity.code);
            prevProps.current.code = selectedSecurity.code;
        }

        if (selectedSecurity.name !== prevProps.current.name && selectedSecurity.name) {
            setSymbolName(selectedSecurity.name);
            prevProps.current.name = selectedSecurity.name;
        }
    }, [props.sliderValue, props.sliderRange, selectedSecurity.code, selectedSecurity.name]);

    // Validate props to prevent errors
    const validateProps = () => {
        try {
            // Reset error state
            setIsSliderError(false);

            // Check if sliderValue and sliderRange exist
            if (!props.sliderValue || !props.sliderRange) {
                setErrorMessage("Slider verisi bulunamadı");
                setIsSliderError(true);
                return false;
            }

            const isValidEndpoint = (val) =>
                typeof val === 'number' ||
                (Array.isArray(val) && val.length && typeof val[0] === 'number');

            if (!isValidEndpoint(props.sliderRange.min) || !isValidEndpoint(props.sliderRange.max)) {
                setErrorMessage('Geçersiz slider veri formatı');
                setIsSliderError(true);
                return false;
            }

            // Get min and max values for validation
            const minValue = Array.isArray(props.sliderRange.min) ? props.sliderRange.min[0] : props.sliderRange.min;
            const maxValue = Array.isArray(props.sliderRange.max) ? props.sliderRange.max[0] : props.sliderRange.max;

            // Log if min and max are equal (we'll handle this gracefully later)
            if (minValue === maxValue) {
                console.warn("Slider min and max values are equal:", minValue);
                // We don't set error here, as we'll handle this case later
            }

            // Check if sliderValue is in proper format (array)
            if (!Array.isArray(props.sliderValue) || props.sliderValue.some(val => typeof val !== 'number')) {
                setErrorMessage("Geçersiz slider değer formatı");
                setIsSliderError(true);
                return false;
            }

            return true;
        } catch (error) {
            console.error("Error validating props:", error);
            setErrorMessage("Slider verileri doğrulanırken hata oluştu");
            setIsSliderError(true);
            return false;
        }
    };

    // Memoize style creation to avoid unnecessary recalculations
    const getStyleContent = React.useMemo(() => {
        return {
            handleStyles: (uniqueId) => `
                    #${uniqueId} .noUi-handle[data-handle="0"] .noUi-tooltip {
                        background-color: #959595 !important;
                        border-color: #959595 !important;
                    }
                    #${uniqueId} .noUi-handle[data-handle="0"] .noUi-tooltip::before {
                        background-color: #959595 !important;
                        border-color: #959595 !important;
                    }
                    #${uniqueId} .noUi-handle[data-handle="1"] .noUi-tooltip {
                        background-color: #42A5F5 !important;
                        border-color: #42A5F5 !important;
                    }
                    #${uniqueId} .noUi-handle[data-handle="1"] .noUi-tooltip::before {
                        background-color: #42A5F5 !important;
                        border-color: #42A5F5 !important;
                    }
                `
        };
    }, []);

    const updateDynamicStyles = (values, handle, unencoded, tap, positions, noUiSlider, uniqueId) => {
        try {
            if (!noUiSlider || !noUiSlider.target) return;

            // Get or create style element
            let dynamicStyles = document.getElementById(`dynamic-styles-${uniqueId}`);
            if (!dynamicStyles) {
                dynamicStyles = document.createElement('style');
                dynamicStyles.id = `dynamic-styles-${uniqueId}`;
                document.head.appendChild(dynamicStyles);
            }

            const tooltipElements = noUiSlider.target.getElementsByClassName('noUi-tooltip');

            // Use our memoized style content
            dynamicStyles.textContent = getStyleContent.handleStyles(uniqueId);

            // Map handles explicitly: index 0 -> average (grey), index 1 -> last (blue)
            if (tooltipElements.length === 2 && values.length === 2) {
                const toNumber = (v) => {
                    if (Array.isArray(v) && v.length) return parseFloat(v[0]);
                    return parseFloat(v);
                };

                const avgVal = toNumber(props.sliderRange?.avg ?? 0);
                const lastVal = toNumber(props.sliderRange?.last ?? 0);
                const avgText = numberFormatter.Double(avgVal, 2);
                const lastText = numberFormatter.Double(lastVal, 2);

                if (tooltipElements[0].textContent !== avgText) {
                    tooltipElements[0].textContent = avgText;
                }

                if (tooltipElements[1] && tooltipElements[1].textContent !== lastText) {
                    tooltipElements[1].textContent = lastText;
                }
            }

            if (values.length === 2 && tooltipElements.length === 2) {
                const tt1 = tooltipElements[0];
                const tt2 = tooltipElements[1];
                const pos1 = parseFloat(positions[0]); // Handle 0 position (%)
                const pos2 = parseFloat(positions[1]); // Handle 1 position (%)

                const roundedAvg = numberFormatter.Double(parseFloat(values[0]), 2);
                const roundedLast = numberFormatter.Double(parseFloat(values[1]), 2);

                // Utility to calculate base edge restraints in X axis
                const getEdgeTranslateX = (pos, offsetPx = 0) => {
                    // Check if the offset pushes it out of bounds and correct it
                    if (pos <= 5) return `calc(-50% + ${Math.max(20, offsetPx)}px)`;
                    if (pos >= 95) return `calc(-50% - ${Math.max(20, -offsetPx)}px)`;

                    if (offsetPx !== 0) {
                        return `calc(-50% + ${offsetPx}px)`;
                    }
                    return `-50%`;
                };

                const PROXIMITY_THRESHOLD_PERCENT = 48;
                const PUSH_OFFSET_PX = 25; // Push apart by 25px each

                const origin1 = tt1.closest('.noUi-origin') || (tt1.parentNode ? tt1.parentNode.parentNode : null);
                const origin2 = tt2.closest('.noUi-origin') || (tt2.parentNode ? tt2.parentNode.parentNode : null);

                if (roundedAvg === roundedLast || Math.abs(pos1 - pos2) < PROXIMITY_THRESHOLD_PERCENT) {
                    // Identify which tooltip is visually on the left
                    const isPos1Left = pos1 <= pos2;

                    // Push them apart side-by-side:
                    // Left tooltip: -100% (moves to the left of its handle)
                    // Right tooltip: -50% (default, centered on its handle)
                    const trans0 = isPos1Left ? '-100%' : '-50%';
                    const trans1 = isPos1Left ? '-50%' : '-100%';

                    // Use setProperty with 'important' priority - this is the ONLY way to 
                    // beat noUiSlider's inline styles with !important
                    tt1.style.setProperty('transform', `translate(${trans0}, 0px)`, 'important');
                    tt2.style.setProperty('transform', `translate(${trans1}, 0px)`, 'important');

                    if (origin1) origin1.style.zIndex = '10';
                    if (origin2) origin2.style.zIndex = '10';
                } else {
                    // Regular constraint
                    tt1.style.setProperty('transform', `translate(${getEdgeTranslateX(pos1)}, 0px)`, 'important');
                    tt2.style.setProperty('transform', `translate(${getEdgeTranslateX(pos2)}, 0px)`, 'important');

                    if (origin1) origin1.style.zIndex = '5';
                    if (origin2) origin2.style.zIndex = '5';
                }

                // Keep only the color styles (not transforms) in the dynamicStyles
                dynamicStyles.textContent = getStyleContent.handleStyles(uniqueId);
            }

        } catch (error) {
            console.error('Error updating dynamic styles:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Hata',
                detail: 'Stiller guncellenirken bir hata olustu',
                life: 3000
            });
        }
    };
    // Memoized calculation of slider values and range to prevent recalculations on every render
    const getSliderConfig = React.useMemo(() => {
        try {
            const toNumber = (v, fallback = 0) => {
                const parseVal = (val) => {
                    if (typeof val === "number" && !Number.isNaN(val)) return val;
                    if (typeof val === "string") {
                        const n = parseFloat(val);
                        return Number.isNaN(n) ? fallback : n;
                    }
                    return fallback;
                };
                if (Array.isArray(v)) {
                    return parseVal(v[0]);
                }
                return parseVal(v);
            };

            const min = toNumber(props.sliderRange?.min, 0);
            const max = toNumber(props.sliderRange?.max, 100);
            const avgValue = toNumber(props.sliderRange?.avg, min);
            const lastValue = toNumber(props.sliderRange?.last, min);

            const safeRange = Math.abs(max - min) < 0.001 ? { min, max: min + 0.1 } : { min, max };
            const clamp = (val) => Math.max(safeRange.min, Math.min(safeRange.max, val));

            const valuesOrdered = [clamp(avgValue), clamp(lastValue)];

            return {
                isValid: true,
                range: safeRange,
                values: valuesOrdered
            };
        } catch (error) {
            console.error("Error calculating slider config:", error);
            return {
                isValid: false,
                error: "Slider hesaplamasi sirasinda hata olustu"
            };
        }
    }, [props.sliderRange?.min, props.sliderRange?.max, props.sliderRange?.avg, props.sliderRange?.last]);

    const renderSlider = () => {
        if (isSliderError) {
            return (
                <div className="slider-error-wrapper" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                    <ErrorWithReload
                        onReload={() => props.onReload && props.onReload()}
                        height="150px"
                        errorMessage={errorMessage}
                    />
                </div>
            );
        }

        const validStart = Array.isArray(props.sliderValue) && props.sliderValue.length > 0;

        if (!validStart) {
            console.warn('GaugeBar: sliderValue boş geldi, Nouislider oluşturulmadı');
            return (
                <div className="slider-error-wrapper" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                    <ErrorWithReload
                        onReload={() => props.onReload && props.onReload()}
                        height="150px"
                    />
                </div>
            );
        }

        try {
            // Use our memoized slider config
            if (!getSliderConfig.isValid) {
                console.warn("Invalid slider configuration:", getSliderConfig.error);
                return (
                    <div className="slider-error-wrapper" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                        <ErrorWithReload
                            onReload={() => props.onReload && props.onReload()}
                            height="150px"
                            errorMessage="Geçersiz veri aralığı"
                        />
                    </div>
                );
            }

            // Final validation check - ensure range is valid
            if (getSliderConfig.range.min >= getSliderConfig.range.max) {
                console.warn("Invalid slider range after adjustments:", getSliderConfig.range);
                return (
                    <div className="slider-error-wrapper" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                        <ErrorWithReload
                            onReload={() => props.onReload && props.onReload()}
                            height="150px"
                            errorMessage="Geçersiz veri aralığı"
                        />
                    </div>
                );
            }

            // Generate a stable key based on props data that actually matters
            // This prevents unnecessary re-renders when other props change
            const sliderKey = `slider-${props.title}-${JSON.stringify(getSliderConfig.values)}-${JSON.stringify(getSliderConfig.range)}`;

            return (
                <Nouislider
                    key={sliderKey}
                    id={`unique-slider-${props.title}`}
                    disabled={true}
                    behaviour="unconstrained"
                    tooltips={true}
                    start={getSliderConfig.values}
                    range={getSliderConfig.range}
                    pips={{
                        mode: 'range',
                        stepped: false,
                        density: 10000,
                        format: {
                            to: function (value) {
                                return numberFormatter.Double(value, 2);
                            },
                            from: function (value) {
                                return Number(value);
                            }
                        }
                    }}
                    onUpdate={(values, handle, unencoded, tap, positions, noUiSlider) => {
                        updateDynamicStyles(values, handle, unencoded, tap, positions, noUiSlider, `unique-slider-${props.title}`);
                    }}
                />
            );
        } catch (error) {
            console.error("Error rendering slider:", error);
            toast.current?.show({
                severity: 'error',
                summary: 'Hata',
                detail: 'Slider çizilirken bir hata oluştu',
                life: 3000
            });
            setIsSliderError(true);
            setErrorMessage("Slider çizilirken hata oluştu");
            return (
                <div className="slider-error-wrapper" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                    <ErrorWithReload
                        onReload={() => props.onReload && props.onReload()}
                        height="150px"
                        errorMessage="Slider çizilirken hata oluştu"
                    />
                </div>
            );
        }
    };

    // Create memoized legend component to prevent unnecessary re-renders
    const MemoizedLegend = React.useMemo(() => {
        try {
            if (!props.data || props.data.length === 0) {
                return null;
            }

            return (
                <Container className='legend-container'>
                    <Row>
                        {props.data.map((item, index) => (
                            <Col xs="3" md="3" className='legend-col' key={index}>
                                <div className='legend-item'>
                                    <div className='color-square' style={{ backgroundColor: item.color }}></div>
                                    <div>
                                        <label>{item.name}</label>
                                        <label>{numberFormatter.Double(item.value, 2)}</label>
                                    </div>
                                </div>
                            </Col>
                        ))}
                    </Row>
                </Container>
            );
        } catch (error) {
            console.error("Error in MemoizedLegend:", error);
            return (
                <div className="legend-error-wrapper" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                    <ErrorWithReload
                        onReload={() => props.onReload && props.onReload()}
                        height="100px"
                        errorMessage="Gösterge yüklenirken hata oluştu"
                    />
                </div>
            );
        }
    }, [props.data]);

    // Final component render using memoized parts where possible
    return (
        <div className="gauge-bar">
            <Toast ref={toast} />
            <div className="gauge-bar-row">
                <div className='title-col'>
                    <label> {props.gaugeLabel} </label>
                </div>
                <div className='gauge-bar-slider-col'>
                    <div className="gauge-bar__slider-value">
                        {renderSlider()}
                    </div>
                </div>
            </div>
            {!isSliderError && MemoizedLegend}
        </div>
    )
}

GaugeBar.propTypes = {
    title: PropTypes.string,
    sliderValue: PropTypes.array,
    sliderRange: PropTypes.shape({
        min: PropTypes.array,
        max: PropTypes.array,
        last: PropTypes.array
    }),
    data: PropTypes.array,
    onReload: PropTypes.func
}

// GaugeBar.defaultProps = {
//     sliderValue: [0],
//     sliderRange: {
//         min: [0],
//         max: [100],
//         last: [50]
//     },
//     data: []
// }

export default GaugeBar;

